import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { ADV_LABELS } from "@/lib/periods";
import { Spinner } from "@/components/Bits";

const Row = ({ k, v, neg }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/[0.05] py-2.5 text-sm last:border-0">
    <span className="text-slate-300">{k}</span><span className={`font-mono-num font-bold ${neg ? "text-red-300" : "text-[#B7F52A]"}`}>{v}</span>
  </div>
);

export function RulesTab({ league }) {
  const r = league.scoring_rule;
  const p = league.current_period;
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="rules-tab">
      <section className="mpl-card p-5">
        <p className="mpl-eyebrow">Barème · {r.label} (version {r.version})</p>
        <div className="mt-3">
          <Row k="Bonne prédiction du vainqueur" v={`+${r.correct_points}`} />
          <Row k="Mauvaise prédiction" v={r.wrong_points} neg={r.wrong_points < 0} />
          {r.streak_bonuses.map((b) => <Row key={b.at} k={`Bonus à ${b.at} succès de suite${b.repeat ? " (à chaque palier)" : ""}`} v={`+${b.points}`} />)}
          <Row k="Pas de pronostic" v="0" />
          <Row k="Match annulé" v="0, série conservée" />
          {league.member_invites && <div data-testid="rules-referral"><Row k="Parrainage : chaque nouveau joueur inscrit avec ton lien perso" v={`+${league.referral_points}`} /></div>}
        </div>
        <p className="mt-3 text-xs text-slate-400">Une mauvaise prédiction interrompt la série ; un match annulé ou une absence ne l'interrompt pas. Chaque palier de bonus n'est attribué qu'une fois par série. Les scores peuvent devenir négatifs.</p>
      </section>
      <section className="mpl-card p-5">
        <p className="mpl-eyebrow">Pronostics bonus</p>
        {r.max_advanced > 0 ? (
          <>
            <p className="mt-2 text-sm text-slate-300">Jusqu'à {r.max_advanced} question(s) facultative(s) par match, identiques pour tous et publiées avant le verrouillage.</p>
            <div className="mt-2">{r.advanced_types.map((t) => <Row key={t} k={ADV_LABELS[t]} v={`+${r.advanced_points[t].win} / ${r.advanced_points[t].lose}`} />)}</div>
            <p className="mt-3 text-xs text-slate-400">Égalité statistique : tous les joueurs à égalité sont de bonnes réponses. Joueur absent ou n'ayant pas joué, match annulé ou statistiques indisponibles : question neutralisée, aucun point. Les questions sur les joueurs nécessitent les statistiques officielles du fournisseur.</p>
          </>
        ) : <p className="mt-2 text-sm text-slate-400">Désactivés pour cette ligue.</p>}
      </section>
      <section className="mpl-card p-5 md:col-span-2">
        <p className="mpl-eyebrow">Compétition</p>
        <p className="mt-2 text-sm text-slate-300">Période principale : <b>{p.type_label}</b> — en cours : {p.label}. Minimum 4 membres pour démarrer, 20 au maximum. Seuls les matchs commençant après le démarrage et après votre arrivée comptent.</p>
        <p className="mt-2 text-sm text-slate-300">Verrouillage : à l'heure officielle de début de chaque match, vérifiée par le serveur. Un match reporté garde votre pronostic jusqu'à sa nouvelle date.</p>
        <p className="mt-2 text-xs text-slate-500">Les règles sont figées dès le démarrage de la compétition. Historique des versions : {(league.rule_history || []).map((h) => `v${h.version} (${h.label})`).join(", ") || "v1"}.</p>
      </section>
    </div>
  );
}

const ACTIONS = {
  "league.created": "Ligue créée", "league.updated": "Ligue modifiée", "league.joined": "Nouveau membre", "league.left": "Départ d'un membre",
  "league.member_removed": "Membre retiré", "league.ownership_transferred": "Propriété transférée", "league.game_excluded": "Match retiré de la sélection",
  "league.game_included": "Match remis dans la sélection", "invitation.created": "Invitation créée", "invitation.revoked": "Invitation révoquée",
  "league.rules_updated": "Barème modifié", "challenge.created": "Défi créé", "challenge.accepted": "Défi accepté", "challenge.declined": "Défi refusé",
  "challenge.cancelled": "Défi annulé", "activity.deleted": "Message supprimé",
};

export function JournalTab({ league }) {
  const { tz } = useAuth();
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get(`/leagues/${league.id}/log`).then(({ data }) => setEvents(data)); }, [league.id]);
  if (!events) return <Spinner />;
  return (
    <div className="mpl-card divide-y divide-white/[0.05]" data-testid="journal-tab">
      {events.map((e, i) => (
        <div key={i} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
          <div className="min-w-0"><p className="font-semibold">{ACTIONS[e.action] || e.action}</p><p className="truncate text-xs text-slate-400">{e.actor}{e.meta?.match ? ` · ${e.meta.match} (${e.meta.date})` : ""}</p></div>
          <p className="shrink-0 text-xs text-slate-500">{fmtDateTime(e.created_at, tz)}</p>
        </div>
      ))}
    </div>
  );
}
